import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircleIcon, UploadCloudIcon } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
type FormData = {
  // Company
  companyName: string;
  tradingName: string;
  yearEstablished: string;
  countryOfRegistration: string;
  companyRegistrationNumber: string;
  vatTaxId: string;
  websiteUrl: string;
  companyType: string;
  // Contact
  fullName: string;
  jobTitle: string;
  email: string;
  phone: string;
  streetAddress: string;
  city: string;
  stateProvince: string;
  postalCode: string;
  country: string;
  // Capabilities
  primarySector: string;
  keyProducts: string;
  certifications: string[];
  otherCertifications: string;
  // Capacity
  monthlyCapacity: string;
  moq: string;
  leadTime: string;
  exportExperience: string;
  marketsServed: string;
  // Commercial
  incoterms: string[];
  paymentTerms: string[];
  currencies: string[];
  // Additional
  additionalInfo: string;
  // Declarations
  confirmAccurate: boolean;
  consentGdpr: boolean;
  consentMarketing: boolean;
};
const initialFormData: FormData = {
  companyName: '',
  tradingName: '',
  yearEstablished: '',
  countryOfRegistration: '',
  companyRegistrationNumber: '',
  vatTaxId: '',
  websiteUrl: '',
  companyType: '',
  fullName: '',
  jobTitle: '',
  email: '',
  phone: '',
  streetAddress: '',
  city: '',
  stateProvince: '',
  postalCode: '',
  country: '',
  primarySector: '',
  keyProducts: '',
  certifications: [],
  otherCertifications: '',
  monthlyCapacity: '',
  moq: '',
  leadTime: '',
  exportExperience: '',
  marketsServed: '',
  incoterms: [],
  paymentTerms: [],
  currencies: [],
  additionalInfo: '',
  confirmAccurate: false,
  consentGdpr: false,
  consentMarketing: false
};
const WEB3FORMS_ENDPOINT = 'https://api.web3forms.com/submit';
const SUPPLIER_ACCESS_KEY = '7f291248-54db-405a-948d-aee1147c1197';
export function SupplierRegistration() {
  useSEO({
    title: 'Supplier Registration',
    description: 'Join our global supplier network. We invite manufacturers, exporters, and distributors to register their company profile, capabilities, and certifications for institutional procurement opportunities.',
    path: '/supplier-registration'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  const handleMultiCheckbox = (field: 'certifications' | 'incoterms' | 'paymentTerms' | 'currencies', value: string) => {
    setFormData(prev => {
      const current = prev[field];
      const updated = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
      return {
        ...prev,
        [field]: updated
      };
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    // Flatten array and boolean fields for cleaner email formatting
    const payload = {
      access_key: SUPPLIER_ACCESS_KEY,
      subject: `New supplier registration: ${formData.companyName}`,
      from_name: '888 Sourcing Website',
      botcheck: '',
      ...formData,
      certifications: formData.certifications.join(', '),
      incoterms: formData.incoterms.join(', '),
      paymentTerms: formData.paymentTerms.join(', '),
      currencies: formData.currencies.join(', '),
      confirmAccurate: formData.confirmAccurate ? 'Yes' : 'No',
      consentGdpr: formData.consentGdpr ? 'Yes' : 'No',
      consentMarketing: formData.consentMarketing ? 'Yes' : 'No'
    };
    try {
      const response = await fetch(WEB3FORMS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data?.success) {
        setIsSuccess(true);
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      } else {
        setErrorMessage(data?.message || 'Something went wrong submitting your registration. Please try again or email us directly at info@888sourcing.co.uk.');
      }
    } catch (err) {
      setErrorMessage('Unable to submit at the moment. Please check your connection or email us directly at info@888sourcing.co.uk.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const inputClass = 'w-full bg-navy-dark border border-slate-700 rounded-md px-4 py-2.5 text-white focus:outline-none focus:border-gold transition-colors';
  const checkboxClass = 'w-4 h-4 rounded border-slate-700 bg-navy-dark text-gold focus:ring-gold focus:ring-offset-navy-dark';
  if (isSuccess) {
    return <div className="pt-32 pb-24 px-6 min-h-screen bg-navy-dark flex items-center justify-center" data-id="element-634">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="max-w-md w-full bg-navy-light border border-slate-800 p-8 rounded-lg text-center" data-id="element-635">
          <div className="w-16 h-16 bg-green-500/10 border border-green-500/30 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-636">
            <CheckCircleIcon size={32} className="text-green-500" data-id="element-637" />
          </div>
          <h2 className="text-2xl font-semibold text-white mb-4" data-id="element-638">
            Registration Received
          </h2>
          <p className="text-slate-400 leading-relaxed mb-8" data-id="element-639">
            Thank you for registering with 888 Sourcing Ltd. Our procurement
            team will review your submission and capabilities. We will contact
            you within 5 business days regarding relevant opportunities.
          </p>
          <button onClick={() => {
          setIsSuccess(false);
          setFormData(initialFormData);
        }} className="bg-navy-dark border border-slate-700 hover:border-gold text-white px-6 py-2.5 rounded-md font-semibold text-sm transition-colors" data-id="element-640">
            Submit Another Registration
          </button>
        </motion.div>
      </div>;
  }
  return <div className="pt-24 pb-24 bg-navy-dark min-h-screen" data-id="element-641">
      {/* Hero Band */}
      <section className="py-16 px-6 bg-navy-light border-b border-slate-800" data-id="element-642">
        <div className="max-w-3xl mx-auto text-center" data-id="element-643">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-644">
            <div className="flex items-center justify-center gap-3 mb-6" data-id="element-645">
              <div className="w-12 h-px bg-gold" data-id="element-646"></div>
              <span className="text-gold text-xs font-semibold tracking-widest uppercase" data-id="element-647">
                Join Our Supplier Network
              </span>
              <div className="w-12 h-px bg-gold" data-id="element-648"></div>
            </div>
            <h1 className="text-4xl lg:text-5xl font-semibold text-white mb-6" data-id="element-649">
              Supplier Registration
            </h1>
            <p className="text-slate-400 text-lg leading-relaxed" data-id="element-650">
              We invite manufacturers, exporters, and distributors to register
              their capabilities. All submissions are reviewed for quality,
              capacity, and compliance against current and upcoming
              institutional procurement requirements.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 px-6" data-id="element-651">
        <div className="max-w-4xl mx-auto" data-id="element-652">
          <form onSubmit={handleSubmit} className="space-y-12" data-id="element-653">
            {/* Section 1: Company Information */}
            <div className="bg-navy-light border border-slate-800 rounded-lg overflow-hidden" data-id="element-654">
              <div className="bg-slate-800/50 border-l-4 border-gold px-6 py-4" data-id="element-655">
                <h2 className="text-lg font-semibold text-white" data-id="element-656">
                  1. Company Information
                </h2>
              </div>
              <div className="p-6 grid sm:grid-cols-2 gap-6" data-id="element-657">
                <div className="sm:col-span-2" data-id="element-658">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-659">
                    Company Name *
                  </label>
                  <input name="companyName" required type="text" value={formData.companyName} onChange={handleChange} className={inputClass} data-id="element-660" />
                </div>
                <div data-id="element-661">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-662">
                    Trading Name (if different)
                  </label>
                  <input name="tradingName" type="text" value={formData.tradingName} onChange={handleChange} className={inputClass} data-id="element-663" />
                </div>
                <div data-id="element-664">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-665">
                    Year Established *
                  </label>
                  <input name="yearEstablished" required type="text" value={formData.yearEstablished} onChange={handleChange} className={inputClass} data-id="element-666" />
                </div>
                <div data-id="element-667">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-668">
                    Country of Registration *
                  </label>
                  <input name="countryOfRegistration" required type="text" value={formData.countryOfRegistration} onChange={handleChange} className={inputClass} data-id="element-669" />
                </div>
                <div data-id="element-670">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-671">
                    Company Registration Number *
                  </label>
                  <input name="companyRegistrationNumber" required type="text" value={formData.companyRegistrationNumber} onChange={handleChange} className={inputClass} data-id="element-672" />
                </div>
                <div data-id="element-673">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-674">
                    VAT / Tax ID
                  </label>
                  <input name="vatTaxId" type="text" value={formData.vatTaxId} onChange={handleChange} className={inputClass} data-id="element-675" />
                </div>
                <div data-id="element-676">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-677">
                    Website URL
                  </label>
                  <input name="websiteUrl" type="text" value={formData.websiteUrl} onChange={handleChange} className={inputClass} placeholder="e.g. www.yourcompany.com" data-id="element-678" />
                </div>
                <div className="sm:col-span-2" data-id="element-679">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-680">
                    Company Type *
                  </label>
                  <select name="companyType" required value={formData.companyType} onChange={handleChange} className={`${inputClass} appearance-none`} data-id="element-681">
                    <option value="" data-id="element-682">Select company type...</option>
                    <option value="manufacturer" data-id="element-683">Manufacturer</option>
                    <option value="exporter" data-id="element-684">Exporter</option>
                    <option value="distributor" data-id="element-685">Distributor</option>
                    <option value="trading" data-id="element-686">Trading Company</option>
                    <option value="service" data-id="element-687">Service Provider</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Section 2: Contact Details */}
            <div className="bg-navy-light border border-slate-800 rounded-lg overflow-hidden" data-id="element-688">
              <div className="bg-slate-800/50 border-l-4 border-gold px-6 py-4" data-id="element-689">
                <h2 className="text-lg font-semibold text-white" data-id="element-690">
                  2. Contact Details
                </h2>
              </div>
              <div className="p-6 grid sm:grid-cols-2 gap-6" data-id="element-691">
                <div data-id="element-692">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-693">
                    Full Name *
                  </label>
                  <input name="fullName" required type="text" value={formData.fullName} onChange={handleChange} className={inputClass} data-id="element-694" />
                </div>
                <div data-id="element-695">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-696">
                    Job Title *
                  </label>
                  <input name="jobTitle" required type="text" value={formData.jobTitle} onChange={handleChange} className={inputClass} data-id="element-697" />
                </div>
                <div data-id="element-698">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-699">
                    Email Address *
                  </label>
                  <input name="email" required type="email" value={formData.email} onChange={handleChange} className={inputClass} data-id="element-700" />
                </div>
                <div data-id="element-701">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-702">
                    Phone Number *
                  </label>
                  <input name="phone" required type="tel" value={formData.phone} onChange={handleChange} className={inputClass} data-id="element-703" />
                </div>
                <div className="sm:col-span-2" data-id="element-704">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-705">
                    Street Address *
                  </label>
                  <input name="streetAddress" required type="text" value={formData.streetAddress} onChange={handleChange} className={inputClass} data-id="element-706" />
                </div>
                <div data-id="element-707">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-708">
                    City *
                  </label>
                  <input name="city" required type="text" value={formData.city} onChange={handleChange} className={inputClass} data-id="element-709" />
                </div>
                <div data-id="element-710">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-711">
                    State / Province
                  </label>
                  <input name="stateProvince" type="text" value={formData.stateProvince} onChange={handleChange} className={inputClass} data-id="element-712" />
                </div>
                <div data-id="element-713">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-714">
                    Postal / Zip Code
                  </label>
                  <input name="postalCode" type="text" value={formData.postalCode} onChange={handleChange} className={inputClass} data-id="element-715" />
                </div>
                <div data-id="element-716">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-717">
                    Country *
                  </label>
                  <input name="country" required type="text" value={formData.country} onChange={handleChange} className={inputClass} data-id="element-718" />
                </div>
              </div>
            </div>

            {/* Section 3: Products & Capabilities */}
            <div className="bg-navy-light border border-slate-800 rounded-lg overflow-hidden" data-id="element-719">
              <div className="bg-slate-800/50 border-l-4 border-gold px-6 py-4" data-id="element-720">
                <h2 className="text-lg font-semibold text-white" data-id="element-721">
                  3. Products & Capabilities
                </h2>
              </div>
              <div className="p-6 space-y-6" data-id="element-722">
                <div data-id="element-723">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-724">
                    Primary Sector *
                  </label>
                  <select name="primarySector" required value={formData.primarySector} onChange={handleChange} className={`${inputClass} appearance-none`} data-id="element-725">
                    <option value="" data-id="element-726">Select primary sector...</option>
                    <option value="construction" data-id="element-727">Construction Materials</option>
                    <option value="industrial" data-id="element-728">Industrial Equipment</option>
                    <option value="medical" data-id="element-729">Medical Supplies</option>
                    <option value="agricultural" data-id="element-730">Agricultural Products</option>
                    <option value="ict" data-id="element-731">ICT & Telecommunications</option>
                    <option value="energy" data-id="element-732">Energy & Infrastructure</option>
                    <option value="consumer" data-id="element-733">
                      Consumer & Commercial Goods
                    </option>
                  </select>
                </div>
                <div data-id="element-734">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-735">
                    Key Products / Brands *
                  </label>
                  <textarea name="keyProducts" required rows={3} value={formData.keyProducts} onChange={handleChange} className={inputClass} placeholder="List your main products, brands, or categories..." data-id="element-736"></textarea>
                </div>
                <div data-id="element-737">
                  <label className="block text-sm font-medium text-slate-300 mb-3" data-id="element-738">
                    Certifications (Select all that apply)
                  </label>
                  <div className="grid sm:grid-cols-3 gap-3" data-id="element-739">
                    {['ISO 9001', 'ISO 14001', 'CE Mark', 'FDA Approved', 'HACCP', 'Halal', 'Kosher', 'Fair Trade'].map(cert => <label key={cert} className="flex items-center gap-2 cursor-pointer" data-id="element-740">
                        <input type="checkbox" checked={formData.certifications.includes(cert)} onChange={() => handleMultiCheckbox('certifications', cert)} className={checkboxClass} data-id="element-741" />
                        <span className="text-sm text-slate-300" data-id="element-742">{cert}</span>
                      </label>)}
                  </div>
                </div>
                <div data-id="element-743">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-744">
                    Other Certifications
                  </label>
                  <input name="otherCertifications" type="text" value={formData.otherCertifications} onChange={handleChange} className={inputClass} placeholder="e.g., specific industry or regional standards" data-id="element-745" />
                </div>
              </div>
            </div>

            {/* Section 4: Production & Capacity */}
            <div className="bg-navy-light border border-slate-800 rounded-lg overflow-hidden" data-id="element-746">
              <div className="bg-slate-800/50 border-l-4 border-gold px-6 py-4" data-id="element-747">
                <h2 className="text-lg font-semibold text-white" data-id="element-748">
                  4. Production & Capacity
                </h2>
              </div>
              <div className="p-6 grid sm:grid-cols-2 gap-6" data-id="element-749">
                <div data-id="element-750">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-751">
                    Monthly Production Capacity
                  </label>
                  <input name="monthlyCapacity" type="text" value={formData.monthlyCapacity} onChange={handleChange} className={inputClass} placeholder="e.g., 10,000 units / 500 MT" data-id="element-752" />
                </div>
                <div data-id="element-753">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-754">
                    Minimum Order Quantity (MOQ)
                  </label>
                  <input name="moq" type="text" value={formData.moq} onChange={handleChange} className={inputClass} data-id="element-755" />
                </div>
                <div data-id="element-756">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-757">
                    Standard Lead Time
                  </label>
                  <input name="leadTime" type="text" value={formData.leadTime} onChange={handleChange} className={inputClass} placeholder="e.g., 30-45 days" data-id="element-758" />
                </div>
                <div data-id="element-759">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-760">
                    Export Experience *
                  </label>
                  <select name="exportExperience" required value={formData.exportExperience} onChange={handleChange} className={`${inputClass} appearance-none`} data-id="element-761">
                    <option value="" data-id="element-762">Select experience level...</option>
                    <option value="new" data-id="element-763">New to export</option>
                    <option value="1-3" data-id="element-764">1-3 years</option>
                    <option value="3-10" data-id="element-765">3-10 years</option>
                    <option value="10+" data-id="element-766">10+ years</option>
                  </select>
                </div>
                <div className="sm:col-span-2" data-id="element-767">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-768">
                    Markets Currently Served
                  </label>
                  <textarea name="marketsServed" rows={2} value={formData.marketsServed} onChange={handleChange} className={inputClass} placeholder="List countries or regions you currently export to..." data-id="element-769"></textarea>
                </div>
              </div>
            </div>

            {/* Section 5: Commercial Terms */}
            <div className="bg-navy-light border border-slate-800 rounded-lg overflow-hidden" data-id="element-770">
              <div className="bg-slate-800/50 border-l-4 border-gold px-6 py-4" data-id="element-771">
                <h2 className="text-lg font-semibold text-white" data-id="element-772">
                  5. Commercial Terms
                </h2>
              </div>
              <div className="p-6 space-y-6" data-id="element-773">
                <div data-id="element-774">
                  <label className="block text-sm font-medium text-slate-300 mb-3" data-id="element-775">
                    Accepted Incoterms
                  </label>
                  <div className="flex flex-wrap gap-4" data-id="element-776">
                    {['EXW', 'FOB', 'CIF', 'CFR', 'DDP'].map(term => <label key={term} className="flex items-center gap-2 cursor-pointer" data-id="element-777">
                        <input type="checkbox" checked={formData.incoterms.includes(term)} onChange={() => handleMultiCheckbox('incoterms', term)} className={checkboxClass} data-id="element-778" />
                        <span className="text-sm text-slate-300" data-id="element-779">{term}</span>
                      </label>)}
                  </div>
                </div>
                <div data-id="element-780">
                  <label className="block text-sm font-medium text-slate-300 mb-3" data-id="element-781">
                    Accepted Payment Terms
                  </label>
                  <div className="flex flex-wrap gap-4" data-id="element-782">
                    {['T/T', 'L/C', 'D/P', 'D/A'].map(term => <label key={term} className="flex items-center gap-2 cursor-pointer" data-id="element-783">
                        <input type="checkbox" checked={formData.paymentTerms.includes(term)} onChange={() => handleMultiCheckbox('paymentTerms', term)} className={checkboxClass} data-id="element-784" />
                        <span className="text-sm text-slate-300" data-id="element-785">{term}</span>
                      </label>)}
                  </div>
                </div>
                <div data-id="element-786">
                  <label className="block text-sm font-medium text-slate-300 mb-3" data-id="element-787">
                    Currency Preferences
                  </label>
                  <div className="flex flex-wrap gap-4" data-id="element-788">
                    {['USD', 'EUR', 'GBP'].map(term => <label key={term} className="flex items-center gap-2 cursor-pointer" data-id="element-789">
                        <input type="checkbox" checked={formData.currencies.includes(term)} onChange={() => handleMultiCheckbox('currencies', term)} className={checkboxClass} data-id="element-790" />
                        <span className="text-sm text-slate-300" data-id="element-791">{term}</span>
                      </label>)}
                  </div>
                </div>
              </div>
            </div>

            {/* Section 6: Supporting Documents */}
            <div className="bg-navy-light border border-slate-800 rounded-lg overflow-hidden" data-id="element-792">
              <div className="bg-slate-800/50 border-l-4 border-gold px-6 py-4" data-id="element-793">
                <h2 className="text-lg font-semibold text-white" data-id="element-794">
                  6. Supporting Documents
                </h2>
              </div>
              <div className="p-6" data-id="element-795">
                <div className="bg-navy-dark border border-slate-800 rounded-md p-5 flex gap-4" data-id="element-796">
                  <UploadCloudIcon size={24} className="text-gold flex-shrink-0 mt-1" data-id="element-797" />
                  <div data-id="element-798">
                    <p className="text-slate-300 text-sm leading-relaxed mb-2" data-id="element-799">
                      After submitting this registration, please email the
                      following documents to{' '}
                      <a href="mailto:info@888sourcing.co.uk" className="text-gold hover:underline" data-id="element-800">
                        info@888sourcing.co.uk
                      </a>{' '}
                      quoting your company name in the subject line:
                    </p>
                    <ul className="text-slate-400 text-sm space-y-1 ml-4 list-disc" data-id="element-801">
                      <li data-id="element-802">Company Profile (PDF)</li>
                      <li data-id="element-803">Product Catalogue (PDF)</li>
                      <li data-id="element-804">Price List (PDF or Excel)</li>
                      <li data-id="element-805">Certifications and Export Licences (PDF or ZIP)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 7: Additional Information */}
            <div className="bg-navy-light border border-slate-800 rounded-lg overflow-hidden" data-id="element-806">
              <div className="bg-slate-800/50 border-l-4 border-gold px-6 py-4" data-id="element-807">
                <h2 className="text-lg font-semibold text-white" data-id="element-808">
                  7. Additional Information
                </h2>
              </div>
              <div className="p-6" data-id="element-809">
                <textarea name="additionalInfo" rows={4} value={formData.additionalInfo} onChange={handleChange} className={inputClass} placeholder="Please provide any additional information about your company, specific projects, or capabilities that would be relevant for institutional procurement..." data-id="element-810"></textarea>
              </div>
            </div>

            {/* Section 8: Declarations */}
            <div className="bg-navy-light border border-slate-800 rounded-lg overflow-hidden" data-id="element-811">
              <div className="bg-slate-800/50 border-l-4 border-gold px-6 py-4" data-id="element-812">
                <h2 className="text-lg font-semibold text-white" data-id="element-813">
                  8. Declarations
                </h2>
              </div>
              <div className="p-6 space-y-4" data-id="element-814">
                <label className="flex items-start gap-3 cursor-pointer" data-id="element-815">
                  <input name="confirmAccurate" required type="checkbox" checked={formData.confirmAccurate} onChange={handleChange} className={`mt-1 ${checkboxClass}`} data-id="element-816" />
                  <span className="text-sm text-slate-300 leading-relaxed" data-id="element-817">
                    I confirm that the information provided in this registration
                    form is accurate and complete to the best of my knowledge. *
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer" data-id="element-818">
                  <input name="consentGdpr" required type="checkbox" checked={formData.consentGdpr} onChange={handleChange} className={`mt-1 ${checkboxClass}`} data-id="element-819" />
                  <span className="text-sm text-slate-300 leading-relaxed" data-id="element-820">
                    I consent to 888 Sourcing Ltd storing and processing this
                    information for the purpose of supplier evaluation and
                    procurement coordination in accordance with the{' '}
                    <Link to="/privacy-policy" className="text-gold hover:underline" data-id="element-821">
                      Privacy Policy
                    </Link>
                    . *
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer" data-id="element-822">
                  <input name="consentMarketing" type="checkbox" checked={formData.consentMarketing} onChange={handleChange} className={`mt-1 ${checkboxClass}`} data-id="element-823" />
                  <span className="text-sm text-slate-300 leading-relaxed" data-id="element-824">
                    I agree to be contacted by 888 Sourcing Ltd regarding
                    relevant procurement opportunities and tender requirements.
                  </span>
                </label>
              </div>
            </div>

            {/* Error Message */}
            {errorMessage && <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm p-4 rounded-md" data-id="element-825">
                {errorMessage}
              </div>}

            {/* Submit Button */}
            <div className="flex justify-end" data-id="element-826">
              <button type="submit" disabled={isSubmitting} className="bg-gold hover:bg-gold-dark disabled:bg-gold/50 disabled:cursor-not-allowed text-navy-dark px-10 py-4 rounded-md font-semibold text-base transition-colors flex items-center gap-2" data-id="element-827">
                {isSubmitting ? 'Submitting...' : 'Submit Registration'}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>;
}